import type { Crop } from '../../react-image-multi-crop';
import type { AspectValue } from '../ReframeAspectDropdown';
import type {
  ILayoutSegment,
  LayoutTemplate,
  TrackTransforms,
} from '@src/editor/models/layout/types';
import type { ElementState } from '@src/renderer/ElementState';
import type React from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type AspectRatioOption = AspectValue;
export type FitOption = 'cover' | 'contain' | 'fill';

export type SlotState = {
  id: string;
  slotName: string;
  /** Scene the slot's crop was read from, and the one Apply writes back to. */
  sceneId: string | undefined;
  elementState: ElementState | null;
  crop: Crop;
  initialCrop: Crop;
  fit: FitOption;
  initialFit: FitOption;
  videoSource: string | undefined;
  videoTime: number;
  aspectRatio: number | undefined;
  selectedAspectRatio: AspectRatioOption;
};

/** What `countScenesFramedDifferently` needs of a `Scene`. */
export type SceneFraming = {
  id: string;
  layout?: { bindings?: ILayoutSegment['bindings'] };
};

export type SlotColorStyle = {
  '--slot-color': string;
} & React.CSSProperties;

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

export const DEFAULT_CROP: Crop = {
  unit: '%',
  x: 0,
  y: 0,
  width: 100,
  height: 100,
};

export const DEFAULT_SLOT_COLOR = '#17c964';

export const SLOT_COLORS = [
  DEFAULT_SLOT_COLOR,
  '#006FEE', // second slot highlight
];

// ─────────────────────────────────────────────────────────────────────────────
// Crop Utilities
// ─────────────────────────────────────────────────────────────────────────────

export const cloneCrop = (crop: Crop | undefined): Crop => ({
  unit: crop?.unit ?? '%',
  x: crop?.x ?? 0,
  y: crop?.y ?? 0,
  width: crop?.width ?? 100,
  height: crop?.height ?? 100,
});

export const ensurePercentCrop = (crop: Crop): Crop => ({
  unit: '%',
  x: crop.x ?? 0,
  y: crop.y ?? 0,
  width: crop.width ?? 100,
  height: crop.height ?? 100,
});

export const clampValue = (value: number, min: number, max: number): number => {
  if (Number.isNaN(value)) {
    return min;
  }
  return Math.min(Math.max(value, min), max);
};

/**
 * Derive the on-screen crop (intersection with the frame) without mutating the
 * stored crop. This prevents ReactCrop from snapping huge/out-of-bounds crops
 * back inside and shifting their centers vertically.
 */
export const clampCropToFrame = (crop: Crop): Crop => {
  const normalized = ensurePercentCrop(crop);
  const x2 = normalized.x + normalized.width;
  const y2 = normalized.y + normalized.height;

  const visibleX1 = clampValue(normalized.x, 0, 100);
  const visibleY1 = clampValue(normalized.y, 0, 100);
  const visibleX2 = clampValue(x2, 0, 100);
  const visibleY2 = clampValue(y2, 0, 100);

  const visibleWidth = Math.max(0, visibleX2 - visibleX1);
  const visibleHeight = Math.max(0, visibleY2 - visibleY1);

  if (visibleWidth === 0 || visibleHeight === 0) {
    return normalized;
  }

  return {
    unit: '%',
    x: visibleX1,
    y: visibleY1,
    width: visibleWidth,
    height: visibleHeight,
  };
};

/**
 * Apply the delta between two display crops back onto the raw crop so we keep
 * out-of-frame offsets intact while responding to user interaction inside the
 * clamped viewport.
 */
export const mergeDisplayDeltaIntoRaw = (
  rawCrop: Crop,
  previousDisplay: Crop,
  nextDisplay: Crop,
): Crop => {
  const deltaX = (nextDisplay.x ?? 0) - (previousDisplay.x ?? 0);
  const deltaY = (nextDisplay.y ?? 0) - (previousDisplay.y ?? 0);
  const deltaW = (nextDisplay.width ?? 0) - (previousDisplay.width ?? 0);
  const deltaH = (nextDisplay.height ?? 0) - (previousDisplay.height ?? 0);

  return ensurePercentCrop({
    ...rawCrop,
    x: (rawCrop.x ?? 0) + deltaX,
    y: (rawCrop.y ?? 0) + deltaY,
    width: Math.max(0, (rawCrop.width ?? 0) + deltaW),
    height: Math.max(0, (rawCrop.height ?? 0) + deltaH),
  });
};

export const cropsEqual = (
  a: Crop,
  b: Crop,
  tolerance: number = 0.001,
): boolean => {
  return (
    Math.abs((a.x ?? 0) - (b.x ?? 0)) <= tolerance &&
    Math.abs((a.y ?? 0) - (b.y ?? 0)) <= tolerance &&
    Math.abs((a.width ?? 0) - (b.width ?? 0)) <= tolerance &&
    Math.abs((a.height ?? 0) - (b.height ?? 0)) <= tolerance
  );
};

/**
 * The crop a scene's slot is framed with, in the same percent space as
 * `SlotState.crop`. Mirrors how `buildSlotState` reads it, full-frame
 * included: a slot with no keyframe is not cropped, it is framed on all of it.
 */
const sceneSlotCrop = (binding: TrackTransforms | undefined): Crop | null => {
  if (!binding) {
    return null;
  }
  const keyframe = binding.transformKeyframes?.[0];
  if (!keyframe) {
    return { unit: '%', x: 0, y: 0, width: 100, height: 100 };
  }
  return {
    unit: '%',
    x: keyframe.crop.x * 100,
    y: keyframe.crop.y * 100,
    width: keyframe.crop.w * 100,
    height: keyframe.crop.h * 100,
  };
};

/**
 * Output ratios the "replaces framing" caution is shown for.
 *
 * Active-speaker framing is not aspect-gated — it runs for 16:9 too — but a
 * 16:9 clip cut from a 16:9 source normally sits at full frame throughout,
 * with no per-speaker framing to lose, so the caution there is noise. The
 * portrait and square crops are where the source is cut into per-speaker
 * scenes and framing one over all of them takes real work away.
 */
const CAUTIONED_ASPECT_RATIOS: readonly string[] = ['9:16', '1:1'];

/** Whether this output ratio gets the caution — see the list above. */
export const framingCautionApplies = (
  aspectRatio: string | undefined,
): boolean => !!aspectRatio && CAUTIONED_ASPECT_RATIOS.includes(aspectRatio);

/**
 * How many scenes "Apply framing to all scenes" would visibly rewrite: those
 * carrying a framing of their own, different from the one in `slots`.
 *
 * What it is for is the warning before that Apply, so it counts only real
 * losses. Excluded are the scene the slots were read from (it is the framing
 * being applied) and any scene whose layout has no slot of that name — there
 * is nowhere to put the crop, so it keeps what it has either way.
 *
 * The comparison is against each slot's `initialCrop`, the framing the scene
 * is *stored* with, never the crop currently being dragged. A clip whose
 * scenes are all framed alike — every 16:9 scene sitting at full frame, say —
 * loses nothing distinctive by being given one framing, however far the
 * creator has since dragged the crop, and a warning there is a false alarm.
 *
 * Nothing here can tell an Auto-Frame Zoom View crop from a hand-dragged one;
 * scenes carry no provenance. Differing framing is the honest signal, and it
 * covers both.
 */
export const countScenesFramedDifferently = (
  scenes: readonly SceneFraming[],
  slots: readonly SlotState[],
  /** Percent crops round-trip through the DOM as floats; below this it is the
   * same framing. */
  tolerance: number = 0.01,
): number => {
  if (slots.length === 0) {
    return 0;
  }

  const framedSceneIds = new Set(slots.map(slot => slot.sceneId));

  return scenes.filter(scene => {
    if (framedSceneIds.has(scene.id)) {
      return false;
    }
    const bindings = scene.layout?.bindings;
    if (!bindings) {
      return false;
    }
    return slots.some(slot => {
      const sceneCrop = sceneSlotCrop(bindings[slot.slotName]);
      return !!sceneCrop && !cropsEqual(sceneCrop, slot.initialCrop, tolerance);
    });
  }).length;
};

/**
 * Convert crop box coordinates to Creatomate center-based coordinates
 */
export const cropToCreatomate = (crop: Crop) => {
  const newWidth = 10000 / crop.width;
  const newHeight = 10000 / crop.height;

  const cropCenterX = crop.x + crop.width / 2;
  const cropCenterY = crop.y + crop.height / 2;

  const newX = 50 - (cropCenterX - 50) * (newWidth / 100);
  const newY = 50 - (cropCenterY - 50) * (newHeight / 100);

  return { x: newX, y: newY, width: newWidth, height: newHeight };
};

/**
 * Compute the actual aspect ratio of a crop based on its percentage dimensions
 * and the output video's aspect ratio.
 *
 * Crop percentages are relative to video dimensions, so we multiply by video aspect:
 * actualAspect = (cropWidth% / cropHeight%) × videoAspect
 */
export const computeCropAspectRatio = (
  crop: Crop,
  videoAspectRatio: number,
): number => {
  return (crop.width / crop.height) * videoAspectRatio;
};

// ─────────────────────────────────────────────────────────────────────────────
// Slot Utilities
// ─────────────────────────────────────────────────────────────────────────────

export const detectAspectRatioFromCrop = (
  crop: Crop,
  cropAspectRatio: number | undefined,
): AspectRatioOption => {
  // Check if crop is in initial/pristine state
  const isInitialCrop =
    crop.x === 0 && crop.y === 0 && crop.width === 100 && crop.height === 100;

  if (isInitialCrop || cropAspectRatio == null) {
    // For pristine crops, default to 'original'
    return 'original';
  }

  // Use generous tolerance to handle rounding errors and minor manual adjustments
  const tolerance = 0.05; // 5% tolerance

  // Check common aspect ratios
  if (Math.abs(cropAspectRatio - 16 / 9) < tolerance) return '16:9';
  if (Math.abs(cropAspectRatio - 9 / 16) < tolerance) return '9:16';
  if (Math.abs(cropAspectRatio - 1) < tolerance) return '1:1';

  return 'custom';
};

export const computeVideoTime = (
  element: Record<string, unknown> | undefined,
  currentTime: number | undefined,
): number => {
  if (!element) {
    return 0;
  }
  const trimStart = parseFloat(String(element?.trim_start ?? 0)) || 0;
  const time = parseFloat(String(element?.time ?? 0)) || 0;
  return trimStart + (currentTime ?? 0) - time;
};

/**
 * How far to shift an absolute playhead time before it means anything to the
 * element `targetId` names inside `container`.
 *
 * A child's `time` is relative to the composition holding it, so after a layout
 * pass — where the originals are hidden inside `layout_composition_*` — an
 * absolute time and a child's `time` are in different bases. Subtracting this
 * puts them back in the same one; `computeVideoTime` is otherwise off by the
 * composition's own start, which seeks the preview to a real but unrelated
 * moment of the source.
 *
 * Accumulates through nesting, and returns `null` when `targetId` isn't inside
 * `container` at all — the caller should then shift by nothing rather than
 * guess.
 */
export const resolveNestedTimeOffset = (
  container: Record<string, unknown> | undefined,
  targetId: string,
): number | null => {
  if (!container) {
    return null;
  }
  if (container.id === targetId) {
    return 0;
  }

  const children = container.elements;
  if (!Array.isArray(children)) {
    return null;
  }

  const inner = parseFloat(String(container.time ?? 0)) || 0;
  for (const child of children) {
    const found = resolveNestedTimeOffset(
      child as Record<string, unknown>,
      targetId,
    );
    if (found !== null) {
      return inner + found;
    }
  }

  return null;
};

export const getSlotColor = (index: number): string => {
  if (index < 0) {
    return DEFAULT_SLOT_COLOR;
  }
  return SLOT_COLORS[index] ?? DEFAULT_SLOT_COLOR;
};

export const computeResetDisabled = (
  slots: Record<string, SlotState>,
): boolean => {
  return Object.values(slots).every(
    slot =>
      cropsEqual(slot.crop, slot.initialCrop) && slot.fit === slot.initialFit,
  );
};

/**
 * Compute aspect ratio for a slot based on layout template dimensions and output video aspect ratio
 */
export const computeSlotAspectRatio = (
  template: LayoutTemplate | undefined,
  slotName: string,
  outputAspectRatio: number | undefined,
): number | undefined => {
  if (!template) {
    return outputAspectRatio;
  }

  // For basic layouts (fill, cover, contain), use fallback
  if (template.type === 'basic') {
    return outputAspectRatio;
  }

  // Find the layout element matching this slot
  const layoutElement = template.layoutElements.find(
    elem => elem.name === slotName,
  );

  if (!layoutElement?.container?.width || !layoutElement?.container?.height) {
    return outputAspectRatio;
  }

  // Parse percentage strings (e.g., "100%" -> 100, "50%" -> 50)
  const widthPercent = parseFloat(
    layoutElement.container.width.toString().replace('%', ''),
  );
  const heightPercent = parseFloat(
    layoutElement.container.height.toString().replace('%', ''),
  );

  if (isNaN(widthPercent) || isNaN(heightPercent) || heightPercent === 0) {
    return outputAspectRatio;
  }

  // Calculate slot's aspect ratio by combining:
  // 1. The slot's dimension ratio (width% / height%)
  // 2. The output video's aspect ratio
  const dimensionRatio = widthPercent / heightPercent;

  if (!outputAspectRatio) {
    return dimensionRatio;
  }

  return dimensionRatio * outputAspectRatio;
};

export const computeNumericAspectRatio = (
  selection: AspectRatioOption,
  videoAspectRatio: number,
): number | undefined => {
  if (selection === 'custom') return undefined;
  if (selection === 'original') return videoAspectRatio;
  const ratios: Record<string, number> = {
    '16:9': 16 / 9,
    '9:16': 9 / 16,
    '1:1': 1,
  };
  return ratios[selection];
};

export const getAspectRatio = (
  ratio: AspectRatioOption,
): number | undefined => {
  const aspectRatios: Record<AspectRatioOption, number | undefined> = {
    '16:9': 16 / 9,
    '9:16': 9 / 16,
    '1:1': 1,
    custom: undefined,
    original: undefined,
  };
  return aspectRatios[ratio];
};
